import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Loader2,
    MessageCircle,
    PhoneCall,
    RefreshCw,
    TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtGBP = (pence) =>
    `£${((pence || 0) / 100).toLocaleString("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })}`;
const fmtGBPfine = (pence) =>
    `£${((pence || 0) / 100).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : "";

const StatCard = ({ label, valuePence, tid }) => (
    <div
        className="border-2 border-neutral-900 bg-white px-5 py-4 flex flex-col"
        data-testid={tid}
    >
        <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500">
            {label}
        </p>
        <p className="font-display font-black text-3xl leading-none mt-1">
            {fmtGBP(valuePence)}
        </p>
    </div>
);

const Sales = () => {
    const [data, setData] = useState(null);
    const [chase, setChase] = useState(null);
    const [upcoming, setUpcoming] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("invoiced"); // "invoiced" | "paid"

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/sales/summary"),
            api.get("/sales/chase"),
            api.get("/sales/upcoming-year"),
        ])
            .then(([s, c, u]) => {
                setData(s.data);
                setChase(c.data);
                setUpcoming(u.data);
            })
            .catch(() => toast.error("Failed to load sales"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center py-16 text-neutral-500">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading…
            </div>
        );
    }

    const monthPence =
        mode === "paid" ? data.month.paid_pence : data.month.invoiced_pence;
    const weekPence =
        mode === "paid" ? data.week.paid_pence : data.week.invoiced_pence;
    const yearPence =
        mode === "paid" ? data.year_totals.paid_pence : data.year_totals.invoiced_pence;

    const outstanding = data.outstanding;
    const overdue = (outstanding.oldest_days || 0) > 30;

    const byServiceTotal = data.by_service.reduce(
        (n, r) => n + (r.invoiced_pence || 0),
        0,
    );

    return (
        <div className="space-y-8" data-testid="sales-page">
            {/* Header */}
            <div className="flex items-end justify-between gap-4">
                <div>
                    <p
                        className="text-[10px] uppercase tracking-widest font-black text-orange-600"
                        data-testid="sales-year-label"
                    >
                        {data.year} · Sales
                    </p>
                    <h1 className="font-display font-black text-4xl sm:text-5xl uppercase leading-none">
                        How's business
                    </h1>
                </div>
                <button
                    onClick={load}
                    className="dh-btn"
                    data-variant="ghost"
                    data-testid="sales-refresh"
                >
                    <RefreshCw size={16} /> Refresh
                </button>
            </div>

            {/* Big monthly hero */}
            <div className="border-2 border-neutral-900 bg-neutral-900 text-white px-6 py-8">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest font-black text-orange-400">
                            This month · {mode === "paid" ? "Paid" : "Invoiced"}
                        </p>
                        <p
                            className="font-display font-black text-6xl sm:text-7xl leading-none mt-2"
                            data-testid="sales-month-total"
                        >
                            {fmtGBP(monthPence)}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 border-2 border-white/40">
                        <button
                            onClick={() => setMode("invoiced")}
                            className={`px-3 py-1.5 text-xs uppercase font-black ${
                                mode === "invoiced"
                                    ? "bg-orange-500 text-white"
                                    : "text-white/70"
                            }`}
                            data-testid="sales-mode-invoiced"
                        >
                            Invoiced
                        </button>
                        <button
                            onClick={() => setMode("paid")}
                            className={`px-3 py-1.5 text-xs uppercase font-black ${
                                mode === "paid"
                                    ? "bg-orange-500 text-white"
                                    : "text-white/70"
                            }`}
                            data-testid="sales-mode-paid"
                        >
                            Paid
                        </button>
                    </div>
                </div>
            </div>

            {/* Week / Year / Materials */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="This week" valuePence={weekPence} tid="sales-week" />
                <StatCard label="This year" valuePence={yearPence} tid="sales-year" />
                <StatCard
                    label="Materials"
                    valuePence={data.year_totals.materials_pence}
                    tid="sales-materials"
                />
                <StatCard
                    label="Gross profit"
                    valuePence={data.year_totals.gross_profit_pence}
                    tid="sales-gross"
                />
            </div>

            {/* Outstanding banner */}
            {outstanding.count > 0 && (
                <Link
                    to="/invoices"
                    className={`block border-2 ${
                        overdue
                            ? "border-red-600 bg-red-50"
                            : "border-orange-600 bg-orange-50"
                    } px-5 py-4`}
                    data-testid="sales-outstanding-banner"
                >
                    <div className="flex items-start gap-4 justify-between">
                        <div>
                            <p
                                className={`text-[10px] uppercase tracking-widest font-black ${
                                    overdue ? "text-red-700" : "text-orange-700"
                                }`}
                            >
                                Outstanding · {outstanding.count} invoice
                                {outstanding.count === 1 ? "" : "s"}
                            </p>
                            <p className="font-display font-black text-3xl mt-1">
                                {fmtGBP(outstanding.total_pence)}
                            </p>
                            {outstanding.oldest_days !== null && (
                                <p
                                    className={`text-xs mt-1 font-bold uppercase tracking-wider ${
                                        overdue ? "text-red-700" : "text-neutral-600"
                                    }`}
                                >
                                    Oldest {outstanding.oldest_days} day
                                    {outstanding.oldest_days === 1 ? "" : "s"} ago
                                    {overdue ? " — chase!" : ""}
                                </p>
                            )}
                        </div>
                        <ArrowUpRight size={20} className="text-neutral-600" />
                    </div>
                </Link>
            )}

            {/* By service */}
            <div>
                <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                    Services this year
                </p>
                {data.by_service.length === 0 ? (
                    <div className="border-2 border-dashed border-neutral-300 px-5 py-10 text-center text-neutral-500">
                        Nothing invoiced yet — go make some money.
                    </div>
                ) : (
                    <div className="border-2 border-neutral-900 bg-white overflow-hidden">
                        <table
                            className="w-full text-sm"
                            data-testid="sales-by-service-table"
                        >
                            <thead>
                                <tr className="bg-neutral-900 text-white text-left uppercase text-xs">
                                    <th className="px-4 py-2">Service</th>
                                    <th className="px-4 py-2 text-right">Jobs</th>
                                    <th className="px-4 py-2 text-right">Revenue</th>
                                    <th className="px-4 py-2 text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_service.map((r) => (
                                    <tr key={r.service_name} className="border-t border-neutral-200">
                                        <td className="px-4 py-2 font-bold">{r.service_name}</td>
                                        <td className="px-4 py-2 text-right">{r.jobs}</td>
                                        <td className="px-4 py-2 text-right">
                                            {fmtGBPfine(r.invoiced_pence)}
                                        </td>
                                        <td className="px-4 py-2 text-right text-neutral-500">
                                            {byServiceTotal > 0
                                                ? `${Math.round((r.invoiced_pence / byServiceTotal) * 100)}%`
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Chase list */}
            <div>
                <div className="flex items-baseline justify-between">
                    <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500">
                        Chase list · reminded 14+ days ago, no reply
                    </p>
                    {chase && (
                        <span className="text-xs font-mono text-neutral-500">
                            {chase.count} to chase
                        </span>
                    )}
                </div>
                <div className="mt-2 space-y-2">
                    {(!chase || chase.count === 0) && (
                        <div className="border-2 border-dashed border-neutral-300 px-5 py-8 text-center text-neutral-500">
                            All caught up. Nobody to nag.
                        </div>
                    )}
                    {chase &&
                        chase.customers.map((c) => (
                            <div
                                key={c.job_id}
                                className="border-2 border-neutral-900 bg-white px-4 py-3 flex items-center gap-3"
                                data-testid={`chase-row-${c.job_id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold truncate">{c.customer_name}</p>
                                    <p className="text-xs text-neutral-500 truncate">
                                        {c.property_address}
                                    </p>
                                    <p className="text-xs text-red-700 font-bold uppercase tracking-wider">
                                        Reminded {c.reminder_days_ago} days ago
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {c.customer_mobile && (
                                        <>
                                            <a
                                                href={`tel:${c.customer_mobile.replace(/\s/g, "")}`}
                                                className="dh-btn"
                                                data-variant="ghost"
                                                data-testid={`chase-call-${c.job_id}`}
                                            >
                                                <PhoneCall size={14} />
                                            </a>
                                            <a
                                                href={`https://wa.me/${c.customer_mobile
                                                    .replace(/\s/g, "")
                                                    .replace(
                                                        /^0/,
                                                        "44",
                                                    )}?text=${encodeURIComponent(
                                                    `Hi ${c.customer_name?.split(" ")[0] || ""}, just checking in about your annual boiler service — happy to book you in this month?`,
                                                )}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="dh-btn"
                                                data-testid={`chase-whatsapp-${c.job_id}`}
                                            >
                                                <MessageCircle size={14} /> WhatsApp
                                            </a>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {/* Upcoming next-year workload */}
            <div>
                <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                    Provisional next-year workload
                </p>
                {!upcoming || upcoming.count === 0 ? (
                    <div className="border-2 border-dashed border-neutral-300 px-5 py-8 text-center text-neutral-500">
                        Nothing forecast beyond 60 days yet.
                    </div>
                ) : (
                    <div className="border-2 border-neutral-900 bg-white px-5 py-4 flex items-center gap-4">
                        <TrendingUp size={32} className="text-orange-600 shrink-0" />
                        <div className="flex-1">
                            <p className="font-display font-black text-2xl leading-none">
                                {upcoming.count} jobs ·{" "}
                                <span className="text-orange-600">
                                    {fmtGBP(upcoming.total_pence)}
                                </span>{" "}
                                queued
                            </p>
                            <p className="text-xs text-neutral-500 mt-1">
                                Recurring services falling due in the next 60–395 days.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Recent invoices */}
            <div>
                <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                    Recent invoices
                </p>
                {data.recent_invoices.length === 0 ? (
                    <div className="border-2 border-dashed border-neutral-300 px-5 py-8 text-center text-neutral-500">
                        No invoices yet.
                    </div>
                ) : (
                    <div className="border-2 border-neutral-900 bg-white">
                        {data.recent_invoices.map((inv) => (
                            <Link
                                key={inv.id}
                                to="/invoices"
                                className="flex items-center gap-3 px-4 py-3 border-t border-neutral-200 first:border-t-0 hover:bg-neutral-50"
                                data-testid={`recent-invoice-${inv.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold truncate">
                                        {inv.customer_name}{" "}
                                        <span className="text-neutral-400 font-mono text-xs">
                                            {inv.number}
                                        </span>
                                    </p>
                                    <p className="text-xs text-neutral-500 truncate">
                                        {inv.service_name} · {fmtDate(inv.issued_at)}
                                    </p>
                                </div>
                                <span
                                    className={`text-xs font-black uppercase tracking-wider px-2 py-1 ${
                                        inv.paid_at
                                            ? "bg-green-100 text-green-800"
                                            : "bg-orange-100 text-orange-800"
                                    }`}
                                >
                                    {inv.paid_at ? "Paid" : "Sent"}
                                </span>
                                <span className="font-black">
                                    {fmtGBPfine(inv.total_pence)}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Sales;
